import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { seo } from "@/lib/seo";
import { LegalPageSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/privacidade")({
  head: () =>
    seo({
      path: "/privacidade",
      title: "Política de privacidade — Seu Bolso Tranquilo",
      description:
        "Como o Seu Bolso Tranquilo coleta, usa e protege seus dados pessoais e financeiros. Transparência, segurança e conformidade com a LGPD.",
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Política de privacidade", path: "/privacidade" },
      ],
    }),
  component: PrivacyPage,
  pendingComponent: LegalPageSkeleton,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-9" />
            <span className="font-display font-bold">Seu Bolso Tranquilo</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" /> Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Legal</p>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-3">
          Política de privacidade
        </h1>
        <p className="text-sm text-muted-foreground mb-10">Última atualização: 21 de abril de 2026</p>

        <div className="space-y-8 text-foreground/90 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">1. Quem somos (controlador dos dados)</h2>
            <p className="text-muted-foreground">
              O Serviço "Seu Bolso Tranquilo" é operado por <strong>Thiago Henrique Ferreira Silva</strong>, que atua como
              controlador dos dados pessoais tratados nesta plataforma, conforme a Lei Geral de Proteção de Dados (LGPD —
              Lei nº 13.709/2018) e legislações aplicáveis.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">2. Quais dados coletamos</h2>
            <p className="text-muted-foreground">
              Coletamos apenas as informações necessárias para o funcionamento do Serviço: (a) dados de cadastro e
              autenticação (e-mail, senha em formato criptografado, identificadores de sessão); (b) dados financeiros
              que você mesmo registra (transações, categorias, metas); (c) dados de uso e telemetria básica (logs de
              acesso, endereço IP, tipo de dispositivo e navegador) para segurança e estabilidade do Serviço.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">3. Como usamos seus dados e bases legais</h2>
            <p className="text-muted-foreground">
              Utilizamos seus dados para autenticar sua conta, exibir suas informações financeiras dentro do aplicativo,
              gerar insights personalizados, prestar suporte e prevenir fraudes. As bases legais aplicáveis são:
              <strong> execução de contrato</strong> (prestação do Serviço que você contratou), <strong>legítimo interesse</strong>{" "}
              (segurança, prevenção a fraudes e melhoria do Serviço), <strong>cumprimento de obrigação legal ou
              regulatória</strong> e, quando aplicável, <strong>consentimento</strong>. Não vendemos nem compartilhamos
              seus dados com terceiros para fins de marketing.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">4. Armazenamento e segurança</h2>
            <p className="text-muted-foreground">
              Seus dados são armazenados em servidores seguros, com criptografia em trânsito e regras estritas de acesso —
              cada usuário consegue ver apenas as próprias informações.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">5. Cookies</h2>
            <p className="text-muted-foreground">
              Utilizamos cookies e tecnologias similares estritamente necessários para manter sua sessão autenticada,
              lembrar suas preferências e garantir o funcionamento básico do Serviço. Não exibimos publicidade no
              aplicativo e não utilizamos cookies, pixels ou identificadores de rastreamento publicitário, redes de
              anúncios ou perfilamento para terceiros.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">6. Seus direitos</h2>
            <p className="text-muted-foreground">
              Você pode, a qualquer momento, acessar, corrigir, atualizar, exportar ou excluir seus dados diretamente pelo
              aplicativo, bem como solicitar a portabilidade, revogar consentimentos e obter informações sobre o tratamento.
              A exclusão da conta remove permanentemente todas as informações associadas.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">7. Compartilhamento com terceiros</h2>
            <p className="text-muted-foreground">
              Compartilhamos dados estritamente necessários com as seguintes categorias de destinatários, sob acordos de
              confidencialidade e segurança:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
              <li>
                <strong>Provedores de infraestrutura</strong> (hospedagem, banco de dados e autenticação), que processam
                dados apenas para viabilizar o funcionamento do Serviço.
              </li>
              <li>
                <strong>Paddle.com Market Limited</strong>, nosso Merchant of Record, que processa pagamentos, gerencia
                assinaturas, calcula impostos, emite faturas e responde por reembolsos. A Paddle recebe os dados
                necessários para concluir e operar a transação (nome, e-mail, país, dados de pagamento). Consulte a{" "}
                <a
                  href="https://www.paddle.com/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-foreground hover:text-primary-glow"
                >
                  política de privacidade da Paddle
                </a>{" "}
                para mais detalhes.
              </li>
              <li>
                <strong>Autoridades públicas</strong>, quando exigido por lei, ordem judicial ou regulação aplicável.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">8. Retenção de dados</h2>
            <p className="text-muted-foreground">
              Mantemos seus dados pelo período necessário para a prestação do Serviço e enquanto sua conta estiver ativa.
              Após a exclusão da conta, os dados pessoais e financeiros associados são eliminados em até 30 dias dos
              nossos sistemas operacionais. Registros mínimos podem ser mantidos por até 5 anos quando exigido por lei
              (por exemplo, obrigações fiscais e contábeis) ou para defesa de direitos em processos administrativos e
              judiciais. Backups são rotacionados periodicamente e expurgados conforme o ciclo de retenção.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">9. Crianças</h2>
            <p className="text-muted-foreground">
              O Serviço não é destinado a menores de 18 anos. Não coletamos intencionalmente dados de crianças.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">10. Alterações nesta política</h2>
            <p className="text-muted-foreground">
              Podemos atualizar esta Política periodicamente. Mudanças relevantes serão comunicadas dentro do aplicativo ou
              por e-mail.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">11. Contato</h2>
            <p className="text-muted-foreground">
              Em caso de dúvidas sobre privacidade ou para exercer seus direitos, entre em contato pelo canal disponibilizado
              dentro do aplicativo.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border/40 px-5 lg:px-8 py-8 mt-10">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          Seu Bolso Tranquilo © 2026
        </div>
      </footer>
    </div>
  );
}
